import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { View, Image, Text, Pressable } from "react-native";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { getFunctions, httpsCallable } from "firebase/functions";
import { auth, firestore, storage } from "../../services/firebase";
import { deleteStoredCatalogProductImage } from "../../services/catalogProductImageStorage";
import { buildCatalogProductImageStoragePath, getStoragePathFromDownloadUrl } from "../../utils/catalogProductImageStorage";
import { firestoreAutoId } from "../../utils/validators";
import AdminAlert from "../AdminAlert";
import AdminSwitch from "../AdminSwitch";
import { SectionCard, Field, FieldLabel, Hint } from "../../pages/AdminPanel/Products/formStyles";
import { ADMIN_COLORS, ADMIN_FONT } from "../../config/theme/adminColors";
import { CatalogEquivalentsPicker } from "../CatalogEquivalentsPicker";
import { ProductCategoryPicker } from "../ProductCategoryPicker";
import { queryClient } from "../../queries/queryClient";
import { useRemoteConfigContext } from "../../contexts/useRemoteConfigContext";
import { useCatalogProducts } from "../../queries/useCatalogProducts";
import { useCategories } from "../../queries/useCategories";
import { useProductCategories } from "../../queries/useProductCategories";
import { useCustomTheme } from "../../contexts/useCustomTheme";
import { useCustomImages } from "../../api/Brand";
import CoverImageAiAssistantButton from "../CoverImageAiAssistantButton";
import CoverAiReferencePickerModal from "../Modals/CoverAiReferencePickerModal";
import { buildProductCoverAiAssistantPayload } from "../../utils/buildProductCoverAiAssistantText";
import {
  buildCatalogModuleCoverReferenceOptions,
} from "../../utils/coverAiReferenceOptions";
import { getInvalidCatalogEquivalents } from "../../utils/catalogContentSignature";
import {
  mergeReverseEquivalentIds,
  syncCatalogEquivalentIds,
} from "../../utils/syncCatalogEquivalentIds";
import { getCatalogVitrinePrimary } from "../../utils/catalogEquivalenceGroup";

import { PRODUCTS, PLANS, COURSES } from "../../constants/entities";

const TYPE_BY_COLLECTION = {
  [PRODUCTS]: "product",
  [PLANS]: "plan",
  [COURSES]: "course",
};

function getCatalogImageDisplayName(url) {
  if (!url) return "";
  const storagePath = getStoragePathFromDownloadUrl(url);
  if (storagePath) return storagePath.split("/").pop() || "";
  try {
    return decodeURIComponent(new URL(url).pathname.split("/").pop() || "");
  } catch {
    return "";
  }
}

/**
 * Seção "Vitrine de Produtos" embutida nos formulários de produto/plano/curso.
 * Persistência via ref (`saveVitrine`) — o modal pai chama no "Salvar alterações".
 */
export const CatalogVitrineFields = forwardRef(function CatalogVitrineFields(
  { collectionName, docId, name, accessIds = [] },
  forwardedRef
) {
  const config = useRemoteConfigContext();
  const { customColor } = useCustomTheme();
  const { data: logoAuth } = useCustomImages("logoAuth");
  const { data: imageApp } = useCustomImages("imageApp");
  const { data: productCategories = [] } = useProductCategories();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadFeedback, setUploadFeedback] = useState(null);
  const [pendingFileName, setPendingFileName] = useState("");
  const [useCoverPickerVisible, setUseCoverPickerVisible] = useState(false);
  const [coverAiFeedback, setCoverAiFeedback] = useState(null);
  const [usingExistingCover, setUsingExistingCover] = useState(false);

  const [catalogImageUrl, setCatalogImageUrl] = useState("");
  const [showInCatalog, setShowInCatalog] = useState(true);
  const [equivalentIds, setEquivalentIds] = useState([]);
  const [productCategoryId, setProductCategoryId] = useState(null);
  const savedEquivalentIdsRef = useRef([]);
  const persistedCatalogImageUrlRef = useRef("");
  const lastSessionUploadUrlRef = useRef(null);
  const catalogImageUrlRef = useRef("");
  const initialMergeDoneRef = useRef(false);
  const fileInputId = `catalog-vitrine-image-${collectionName}-${docId}`;

  const { data: catalogProducts = [] } = useCatalogProducts(!!docId);
  const { data: categories = [], isLoading: categoriesLoading } = useCategories();

  const productCategoryName = useMemo(() => {
    if (!productCategoryId) return null;
    return productCategories.find((category) => category.id === productCategoryId)?.name ?? null;
  }, [productCategoryId, productCategories]);

  const selfProduct = catalogProducts.find((item) => item.id === docId);

  const catalogModuleCoverOptions = useMemo(
    () =>
      buildCatalogModuleCoverReferenceOptions(categories, {
        selfProduct,
        accessIds,
      }),
    [categories, selfProduct, accessIds]
  );

  const coverAiVisualContext = useMemo(
    () => ({
      colors: customColor,
      logoAuth,
      imageApp,
      categories,
      catalogProducts,
    }),
    [customColor, logoAuth, imageApp, categories, catalogProducts]
  );

  const vitrinePrimary = selfProduct ? getCatalogVitrinePrimary(selfProduct, catalogProducts) : null;
  const isLinkedSecondary = !!selfProduct && !!vitrinePrimary && vitrinePrimary.id !== docId;
  const currentImageName =
    pendingFileName || getCatalogImageDisplayName(catalogImageUrl) || "";

  useEffect(() => {
    catalogImageUrlRef.current = catalogImageUrl;
  }, [catalogImageUrl]);

  useEffect(() => {
    initialMergeDoneRef.current = false;
    persistedCatalogImageUrlRef.current = "";
    lastSessionUploadUrlRef.current = null;
    catalogImageUrlRef.current = "";
    setPendingFileName("");
  }, [docId, collectionName]);

  useEffect(() => {
    if (!docId || loading || initialMergeDoneRef.current || catalogProducts.length === 0) return;

    const selfType = TYPE_BY_COLLECTION[collectionName];
    if (!selfType) return;

    const merged = mergeReverseEquivalentIds(
      catalogProducts,
      { id: docId, type: selfType },
      savedEquivalentIdsRef.current
    );

    setEquivalentIds(merged);
    initialMergeDoneRef.current = true;
  }, [docId, collectionName, loading, catalogProducts]);

  useEffect(() => {
    if (!docId) return;
    let cancelled = false;

    setLoading(true);
    getDoc(doc(firestore, collectionName, docId))
      .then((snap) => {
        if (cancelled || !snap.exists()) return;
        const data = snap.data();
        const loadedImageUrl = data.catalogImage?.url || "";
        setCatalogImageUrl(loadedImageUrl);
        persistedCatalogImageUrlRef.current = loadedImageUrl;
        lastSessionUploadUrlRef.current = null;
        setPendingFileName("");
        const loadedEquivalents = Array.isArray(data.equivalentIds) ? data.equivalentIds : [];
        savedEquivalentIdsRef.current = loadedEquivalents;
        setEquivalentIds(loadedEquivalents);
        setProductCategoryId(data.productCategoryId ?? null);
        setShowInCatalog(data.showInCatalog !== false);
      })
      .catch((error) => {
        console.error("Erro ao carregar configuração da vitrine:", error);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [collectionName, docId]);

  const saveVitrine = async () => {
    if (!docId) return { ok: true };

    // Vitrine desligada: grava o que foi preenchido (preparação para ativar depois),
    // mas não exige capa/categoria — quem não usa a Vitrine só quer editar o produto.
    const enforceRequired = !!config.hasProductCatalog;

    if (uploading) {
      return {
        ok: false,
        variant: "error",
        message: "Aguarde o envio da imagem terminar antes de salvar.",
      };
    }

    setSaving(true);
    try {
      if (!isLinkedSecondary) {
        const invalidEquivalents = getInvalidCatalogEquivalents(
          selfProduct,
          equivalentIds,
          catalogProducts,
          categories
        );

        if (invalidEquivalents.length > 0) {
          return {
            ok: false,
            variant: "error",
            message:
              "Remova os equivalentes com conteúdo diferente antes de salvar. Só é possível vincular itens com os mesmos módulos.",
          };
        }

        if (enforceRequired && showInCatalog && !catalogImageUrl) {
          return {
            ok: false,
            variant: "error",
            message: "Adicione uma imagem de capa na Vitrine de Produtos antes de salvar.",
          };
        }

        if (enforceRequired && showInCatalog && !productCategoryId) {
          return {
            ok: false,
            variant: "error",
            message: "Selecione uma categoria de produtos na Vitrine de Produtos antes de salvar.",
          };
        }
      }

      const vitrinePayload = isLinkedSecondary
        ? {
            equivalentIds,
            showInCatalog: false,
            catalogImage: null,
            productCategoryId: null,
          }
        : {
            showInCatalog,
            catalogImage: catalogImageUrl ? { url: catalogImageUrl } : null,
            equivalentIds,
            productCategoryId,
          };

      await updateDoc(doc(firestore, collectionName, docId), vitrinePayload);

      if (
        persistedCatalogImageUrlRef.current &&
        persistedCatalogImageUrlRef.current !== catalogImageUrl
      ) {
        await deleteStoredCatalogProductImage(persistedCatalogImageUrlRef.current);
      }

      persistedCatalogImageUrlRef.current = catalogImageUrl || "";
      lastSessionUploadUrlRef.current = null;
      setPendingFileName(getCatalogImageDisplayName(catalogImageUrl));

      await syncCatalogEquivalentIds({
        collectionName,
        docId,
        previousEquivalentIds: savedEquivalentIdsRef.current,
        nextEquivalentIds: equivalentIds,
      });
      savedEquivalentIdsRef.current = equivalentIds;
      queryClient.invalidateQueries({ queryKey: ["catalogProducts"] });
      return { ok: true };
    } catch (error) {
      console.error("Erro ao salvar configuração da vitrine:", error);
      return {
        ok: false,
        variant: "error",
        message: `Erro ao salvar a vitrine (${error?.code || error?.message || "desconhecido"}).`,
      };
    } finally {
      setSaving(false);
    }
  };

  useImperativeHandle(forwardedRef, () => ({
    saveVitrine,
    isBusy: () => saving || uploading,
  }));

  const buildProductCoverPrompt = (selectedReference) =>
    buildProductCoverAiAssistantPayload({
      productTitle: name || selfProduct?.name || "",
      productCategoryName,
      catalogProducts,
      catalogModuleCovers: catalogModuleCoverOptions,
      excludeProductId: docId,
      equivalentIds,
      visualContext: coverAiVisualContext,
      selectedReference,
    });

  /**
   * Substitui `catalogImageUrl` por uma imagem recém-enviada, limpando
   * uploads anteriores desta sessão que ficaram órfãos. Compartilhado pelo
   * upload manual e por "Usar esta capa" (capa de módulo copiada para a
   * pasta de imagens de produto).
   */
  const adoptNewCatalogImage = async (downloadURL) => {
    const previousPreviewUrl = catalogImageUrlRef.current;

    if (
      lastSessionUploadUrlRef.current &&
      lastSessionUploadUrlRef.current !== downloadURL
    ) {
      await deleteStoredCatalogProductImage(lastSessionUploadUrlRef.current);
    }

    if (
      previousPreviewUrl &&
      previousPreviewUrl !== downloadURL &&
      previousPreviewUrl !== persistedCatalogImageUrlRef.current
    ) {
      await deleteStoredCatalogProductImage(previousPreviewUrl);
    }

    lastSessionUploadUrlRef.current = downloadURL;
    setCatalogImageUrl(downloadURL);
    setPendingFileName(getCatalogImageDisplayName(downloadURL));
  };

  /**
   * "Usar esta capa": pede pro backend copiar o arquivo do módulo de
   * referência direto no Storage (bucket.file().copy(), sem passar pelo
   * navegador) para uma imagem PRÓPRIA do produto (pasta `products/`) — fica
   * duplicada de propósito, para não ter que rastrear compartilhamento entre
   * catálogo e vitrine. Um fetch() da URL de download a partir do navegador
   * esbarrava em CORS; copiar no servidor evita isso por completo.
   */
  const handleUseModuleCoverAsProductImage = async (selectedReference) => {
    if (!selectedReference?.url || uploading) return;

    if (!auth.currentUser) {
      throw new Error("Sessão expirada. Faça login novamente antes de enviar a imagem.");
    }

    const sourceStoragePath = getStoragePathFromDownloadUrl(selectedReference.url);
    if (!sourceStoragePath) {
      throw new Error("Não foi possível identificar o arquivo desta capa.");
    }

    setUploadFeedback(null);
    setUploading(true);
    setUploadProgress(0);
    try {
      const functions = getFunctions();
      const copyModuleCoverToProducts = httpsCallable(functions, "copyModuleCoverToProducts");
      const result = await copyModuleCoverToProducts({ sourceStoragePath });
      const downloadURL = result?.data?.url;
      if (!downloadURL) throw new Error("Resposta inválida do servidor.");

      await adoptNewCatalogImage(downloadURL);
    } finally {
      setUploading(false);
    }
  };

  const handleConfirmUseExistingCover = async (selectedOption) => {
    if (!selectedOption || usingExistingCover) return;

    setUsingExistingCover(true);
    try {
      await handleUseModuleCoverAsProductImage(selectedOption);
      setUseCoverPickerVisible(false);
    } catch (error) {
      console.error("Erro ao usar capa existente do módulo:", error);
      setUploadFeedback({
        variant: "error",
        message: `Não foi possível usar esta capa (${error?.message || error?.code || "erro desconhecido"}).`,
      });
    } finally {
      setUsingExistingCover(false);
    }
  };

  const handleUploadImage = (event) => {
    const file = event?.target?.files?.[0];
    const input = event?.target;
    if (!file || uploading) return;

    if (!auth.currentUser) {
      setUploadFeedback({
        variant: "error",
        message: "Sessão expirada. Faça login novamente antes de enviar a imagem.",
      });
      return;
    }

    setUploadFeedback(null);
    setPendingFileName(file.name);
    setUploading(true);
    setUploadProgress(0);

    const resetFileInput = () => {
      if (input) input.value = "";
    };

    let uploadTask;
    try {
      const storagePath = buildCatalogProductImageStoragePath(file.name, firestoreAutoId);
      const storageRef = ref(storage, storagePath);
      uploadTask = uploadBytesResumable(storageRef, file, {
        contentType: file.type || "image/png",
      });
    } catch (error) {
      console.error("Erro ao iniciar o upload da imagem da vitrine:", error);
      setPendingFileName(getCatalogImageDisplayName(catalogImageUrlRef.current));
      setUploading(false);
      resetFileInput();
      setUploadFeedback({
        variant: "error",
        message: `Erro ao iniciar o envio (${error?.code || error?.message || "desconhecido"}).`,
      });
      return;
    }

    uploadTask.on(
      "state_changed",
      (snapshot) => {
        if (!snapshot.totalBytes) return;
        setUploadProgress(
          Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100)
        );
      },
      (error) => {
        console.error("Erro no upload da imagem da vitrine:", error);
        setPendingFileName(getCatalogImageDisplayName(catalogImageUrlRef.current));
        setUploading(false);
        resetFileInput();
        setUploadFeedback({
          variant: "error",
          message: `Erro ao enviar a imagem (${error?.code || "desconhecido"}).`,
        });
      },
      async () => {
        try {
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
          await adoptNewCatalogImage(downloadURL);
          setUploading(false);
          resetFileInput();
        } catch (error) {
          console.error("Erro ao obter URL da imagem da vitrine:", error);
          setPendingFileName(getCatalogImageDisplayName(catalogImageUrlRef.current));
          setUploading(false);
          resetFileInput();
          setUploadFeedback({
            variant: "error",
            message: `Erro ao finalizar o envio (${error?.code || "desconhecido"}).`,
          });
        }
      }
    );
  };

  if (!docId) return null;

  const vitrineOff = !config.hasProductCatalog;

  if (isLinkedSecondary) {
    const primaryName = vitrinePrimary?.name || "outro produto";
    return (
      <SectionCard title="Vitrine de Produtos">
        <AdminAlert variant="info">
          Este produto está vinculado ao produto{" "}
          <Text style={{ fontFamily: ADMIN_FONT, fontWeight: "600" }}>{primaryName}</Text>.
          {" "}A imagem de capa e a categoria são configuradas nele.
        </AdminAlert>
      </SectionCard>
    );
  }

  return (
    <SectionCard
      title="Vitrine de Produtos"
      subtitle={`Configure como ${name || "este item"} aparece na vitrine.`}
    >
      {vitrineOff && (
        <AdminAlert variant="info">
          A Vitrine de Produtos está desativada. Preencher capa e categoria é opcional agora, mas
          necessário para ativá-la em Funcionalidades.
        </AdminAlert>
      )}
      <Field>
        <FieldLabel>Exibir na vitrine</FieldLabel>
        <Hint>
          Desative para ocultar este produto da vitrine na Home. A capa e a categoria
          permanecem salvas para quando você reativar.
        </Hint>
        <View style={{ marginTop: 8, flexDirection: "row", alignItems: "center", gap: 10 }}>
          <AdminSwitch value={showInCatalog} onValueChange={setShowInCatalog} />
          <Text style={{ fontFamily: ADMIN_FONT, fontSize: 13, color: ADMIN_COLORS.muted }}>
            {showInCatalog ? "Visível na vitrine" : "Oculto da vitrine"}
          </Text>
        </View>
      </Field>

      <Field>
        <FieldLabel>Imagem da vitrine</FieldLabel>
        <Hint>
          {vitrineOff ? "Necessária para ativar a Vitrine" : "Obrigatória"} — o produto só aparece na vitrine com a capa definida.
        </Hint>
        {!!catalogImageUrl && (
          <Image
            source={{ uri: catalogImageUrl }}
            style={{
              width: 160,
              height: 90,
              borderRadius: 8,
              marginTop: 10,
              borderWidth: 1,
              borderColor: ADMIN_COLORS.border,
            }}
            resizeMode="cover"
          />
        )}
        {!!currentImageName && (
          <Text
            style={{
              fontFamily: ADMIN_FONT,
              fontSize: 12,
              color: ADMIN_COLORS.muted,
              marginTop: catalogImageUrl ? 8 : 10,
            }}
          >
            Capa atual: {currentImageName}
          </Text>
        )}
        <View style={{ marginTop: 10, alignItems: "flex-start", gap: 8, flexDirection: "row", flexWrap: "wrap" }}>
          <input
            id={fileInputId}
            type="file"
            accept="image/*"
            onChange={handleUploadImage}
            disabled={loading}
            style={{ display: "none" }}
          />
          <label
            htmlFor={uploading ? undefined : fileInputId}
            style={{
              cursor: loading || uploading ? "not-allowed" : "pointer",
              opacity: loading || uploading ? 0.6 : 1,
              pointerEvents: loading || uploading ? "none" : "auto",
            }}
          >
            <View
              style={{
                paddingVertical: 8,
                paddingHorizontal: 14,
                borderRadius: 10,
                borderWidth: 1,
                borderColor: ADMIN_COLORS.border,
                backgroundColor: ADMIN_COLORS.surface,
              }}
            >
              <Text
                style={{
                  fontFamily: ADMIN_FONT,
                  fontSize: 13,
                  fontWeight: "600",
                  color: ADMIN_COLORS.accent,
                }}
              >
                {catalogImageUrl ? "Alterar imagem" : "Selecionar imagem"}
              </Text>
            </View>
          </label>
          <CoverImageAiAssistantButton
            label="Gerar capa com IA"
            disabled={loading || categoriesLoading || uploading}
            getReferenceOptions={() => catalogModuleCoverOptions}
            buildPrompt={buildProductCoverPrompt}
            pickerTitle="Escolher capa de referência"
            pickerSubtitle="Selecione a capa de um módulo do catálogo deste produto. O prompt será copiado e a imagem escolhida será baixada para anexar na IA da sua preferência (ChatGPT, Claude ou similar)."
            onFeedback={setCoverAiFeedback}
          />
          <Pressable
            onPress={() => setUseCoverPickerVisible(true)}
            disabled={loading || categoriesLoading || uploading || catalogModuleCoverOptions.length === 0}
            style={({ hovered }) => [
              {
                paddingVertical: 8,
                paddingHorizontal: 14,
                borderRadius: 10,
                borderWidth: 1,
                borderColor: ADMIN_COLORS.border,
                backgroundColor: ADMIN_COLORS.surface,
                cursor: "pointer",
              },
              hovered && { backgroundColor: ADMIN_COLORS.surfaceSubtle },
              (loading || categoriesLoading || uploading || catalogModuleCoverOptions.length === 0) && {
                opacity: 0.6,
              },
            ]}
          >
            <Text
              style={{
                fontFamily: ADMIN_FONT,
                fontSize: 13,
                fontWeight: "600",
                color: ADMIN_COLORS.accent,
              }}
            >
              Usar capa existente
            </Text>
          </Pressable>
        </View>
        {coverAiFeedback && (
          <AdminAlert
            variant={coverAiFeedback.type}
            onClose={() => setCoverAiFeedback(null)}
          >
            {coverAiFeedback.text1}
          </AdminAlert>
        )}
        <CoverAiReferencePickerModal
          visible={useCoverPickerVisible}
          title="Usar capa de um módulo"
          subtitle="Selecione a capa de um módulo do catálogo deste produto. A imagem será copiada e usada diretamente como capa da vitrine."
          options={catalogModuleCoverOptions}
          onConfirm={handleConfirmUseExistingCover}
          onDismiss={() => {
            if (!usingExistingCover) setUseCoverPickerVisible(false);
          }}
          confirmLabel="Usar esta capa"
          isLoading={usingExistingCover}
          errorMessage={uploadFeedback?.message}
        />
        {uploading && <Hint>Enviando imagem... {uploadProgress}%</Hint>}
        {!catalogImageUrl && !uploading && !loading && (
          <Hint>Nenhuma imagem selecionada.</Hint>
        )}
        {!categoriesLoading && catalogModuleCoverOptions.length === 0 ? (
          <Hint>
            Nenhum módulo com capa vinculado — "Gerar capa com IA" usará só a identidade visual do
            app (sem template para anexar), e "Usar capa existente" fica indisponível.
          </Hint>
        ) : null}
      </Field>

      <Field>
        <FieldLabel>Categoria de produtos</FieldLabel>
        <Hint>
          {vitrineOff ? "Necessária para ativar a Vitrine" : "Obrigatória"} — o produto só aparece na vitrine com uma categoria selecionada
          (ex.: &quot;Cursos teóricos&quot;, &quot;Formações&quot;).
        </Hint>
        <View style={{ marginTop: 8 }}>
          <ProductCategoryPicker compact value={productCategoryId} onChange={setProductCategoryId} />
        </View>
      </Field>

      <Field style={{ marginBottom: 0 }}>
        <FieldLabel>Produtos equivalentes</FieldLabel>
        <Hint>
          Evite produtos repetidos na vitrine. Vincule aqui outros produtos, planos ou cursos que
          liberam exatamente o mesmo conteúdo (ex.: turmas diferentes de um curso ou formas de
          pagamento diferentes, como à vista e parcelado). Na página principal da Vitrine, o aluno
          vê uma única imagem de capa para todos os produtos vinculados.
        </Hint>
        <View style={{ marginTop: 8 }}>
          <CatalogEquivalentsPicker
            selfId={docId}
            value={equivalentIds}
            onChange={setEquivalentIds}
          />
        </View>
      </Field>

      {uploadFeedback && (
        <View style={{ marginTop: 12 }}>
          <AdminAlert variant={uploadFeedback.variant}>{uploadFeedback.message}</AdminAlert>
        </View>
      )}
    </SectionCard>
  );
});

export default CatalogVitrineFields;
